/**
 * Applies a draft tenant theme to this document — this tab — only, without saving; a reload drops it
 * (feature plan 0054).
 *
 * The draft goes into a constructed `CSSStyleSheet` adopted by the document. Adopted sheets come
 * after every document sheet in the cascade, so the draft wins like the saved theme does. The saved
 * theme `<link>` is switched off meanwhile, so a variable the draft no longer sets falls back to the
 * default, as it will after saving.
 *
 * Relative URLs: the theme holds CSS VARIABLES only, and the browser resolves a `url(…)` inside a
 * variable where the variable is USED — in the app's own stylesheets under /assets/css/ — not where
 * it is declared (measured live on srv-999, Chrome 154: `--icon__status-in-progress:
 * url(../img/Bell.gif)` from the preview resolves to /assets/img/Bell.gif through
 * dialog-issue-edit.css). Preview and saved file therefore resolve alike. The `baseURL` passed here is
 * only belt and braces; the same measurement showed Chrome ignoring it for the sheet's own rules.
 *
 * The document is injected so the logic is testable under vitest's node environment.
 */

export interface PreviewDocument {
    getElementById(id: string): unknown;
    adoptedStyleSheets: CSSStyleSheet[];
}

export type StyleSheetFactory = (options: { baseURL: string }) => CSSStyleSheet;

type ThemeLink = { sheet: { disabled: boolean } | null; disabled: boolean };

export const TENANT_THEME_LINK_ID = 'tenant-theme';

export const TENANT_THEME_PATH = '/assets/theme/tenant-theme';

export class TenantThemePreview {

    private _sheet: CSSStyleSheet | null = null;

    /** The link's state before the preview started; `null` while no preview runs. */
    private _saved: { viaSheet: boolean; disabled: boolean } | null = null;

    public constructor(
        private readonly _document: PreviewDocument,
        private readonly _origin: string,
        private readonly _createSheet: StyleSheetFactory,
    ) {
    }

    public get active(): boolean {
        return this._sheet !== null;
    }

    public start(draft: string): void {
        if (this._saved === null) {
            const link = this._link();
            if (link) {
                const viaSheet = link.sheet !== null;
                this._saved = { viaSheet, disabled: viaSheet ? link.sheet!.disabled : link.disabled };
                if (viaSheet) {
                    link.sheet!.disabled = true;
                } else {
                    link.disabled = true;
                }
            } else {
                this._saved = { viaSheet: false, disabled: false };
            }
        }

        if (this._sheet === null) {
            this._sheet = this._createSheet({ baseURL: this._origin + TENANT_THEME_PATH });
            this._document.adoptedStyleSheets = [...this._document.adoptedStyleSheets, this._sheet];
        }
        this._sheet.replaceSync(draft);
    }

    /** Ends the preview: the link gets its earlier state back, the draft sheet goes. No-op without a start. */
    public stop(): void {
        this.restoreLink();
        if (this._sheet !== null) {
            const sheet = this._sheet;
            this._document.adoptedStyleSheets = this._document.adoptedStyleSheets.filter(s => s !== sheet);
            this._sheet = null;
        }
    }

    /**
     * Gives the link its earlier state back but keeps the draft sheet. Called before the link's href
     * changes after a save: a link with the `disabled` attribute does not load at all, so its `load`
     * event — which ends the preview without a flash — would never come.
     */
    public restoreLink(): void {
        if (this._saved === null) {
            return;
        }
        const link = this._link();
        if (link) {
            // Write onto the CURRENT sheet: after an href change the link holds a new one.
            if (this._saved.viaSheet && link.sheet) {
                link.sheet.disabled = this._saved.disabled;
            } else {
                link.disabled = this._saved.disabled;
            }
        }
        this._saved = null;
    }

    private _link(): ThemeLink | null {
        return (this._document.getElementById(TENANT_THEME_LINK_ID) as ThemeLink | null) ?? null;
    }
}
